import React, { useState, useEffect } from 'react';
import { View, Pressable, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@constants/Themed';
import { AntDesign } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import AnimatedLottieView from '@utils/lottieAnimation';
import type { PlanQuestion } from '@utils/planQuestionParser';

interface QuestionMessageProps {
  currentMessage: any; // GiftedChat message with planQuestions
  theme: boolean;
  onSubmit: (answers: Record<string, string[]>) => void;
  styles: any;
}

const QuestionMessage: React.FC<QuestionMessageProps> = React.memo(({
  currentMessage,
  theme,
  onSubmit,
  styles
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string[]>>({});
  const questions: PlanQuestion[] = currentMessage.planQuestions || [];

  // Initialize selected answers from message if available
  useEffect(() => {
    if (currentMessage.selectedAnswers) {
      setSelectedAnswers(currentMessage.selectedAnswers);
    }
  }, [currentMessage.selectedAnswers]);

  const handleOptionToggle = (questionId: string, option: string) => {
    setSelectedAnswers(prev => {
      const currentSelections = prev[questionId] || [];
      const question = questions.find(q => q.id === questionId);
      const allowMultiple = question?.allowMultiple !== false;

      if (allowMultiple) {
        // Multi-select: toggle option
        if (currentSelections.includes(option)) {
          return {
            ...prev,
            [questionId]: currentSelections.filter(opt => opt !== option)
          };
        } else {
          return {
            ...prev,
            [questionId]: [...currentSelections, option]
          };
        }
      } else {
        // Single-select: replace selection
        return {
          ...prev,
          [questionId]: currentSelections.includes(option) ? [] : [option]
        };
      }
    });
  };

  const handleSubmit = () => {
    onSubmit(selectedAnswers);
  };

  const isOptionSelected = (questionId: string, option: string): boolean => {
    return (selectedAnswers[questionId] || []).includes(option);
  };

  const canSubmit = (): boolean => {
    // Check if all required questions have at least one answer
    return questions.every(question => {
      if (question.required === false) return true;
      const selections = selectedAnswers[question.id] || [];
      return selections.length > 0;
    });
  };

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <Animatable.View
      useNativeDriver
      style={[styles.systemMessageContainer, { marginBottom: 20 }]}
      animation={'fadeInUp'}
    >
      <AnimatedLottieView
        source={require("@assets/animations/automation_robot.json")}
        autoPlay
        loop
        style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-start", marginTop: 10 }}
      />
      <View style={[styles.systemMessageBubble, { 
        backgroundColor: theme ? '#f8f9fa' : '#2a2a2a', 
        width: '90%', 
        maxWidth: '90%', 
        padding: 16 
      }]}>
        {/* Header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
          <AntDesign name="questioncircle" size={18} color={theme ? '#3b82f6' : '#60a5fa'} style={{ marginRight: 8 }} />
          <Text style={{ fontWeight: '600', fontSize: 16, color: theme ? '#374151' : '#e5e7eb' }}>
            Please Answer These Questions
          </Text>
        </View>

        {/* Questions List */}
        <ScrollView 
          style={{ maxHeight: 400 }} 
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
        >
          {questions.map((question, questionIndex) => {
            const isMultiSelect = question.allowMultiple !== false;
            const selections = selectedAnswers[question.id] || [];

            return (
              <View 
                key={question.id} 
                style={{ 
                  marginBottom: questionIndex < questions.length - 1 ? 24 : 0,
                  paddingBottom: questionIndex < questions.length - 1 ? 24 : 0,
                  borderBottomWidth: questionIndex < questions.length - 1 ? 1 : 0,
                  borderBottomColor: theme ? '#e5e7eb' : '#374151'
                }}
              >
                {/* Question Text */}
                <View style={{ marginBottom: 12 }}>
                  <Text style={{ 
                    fontSize: 15, 
                    fontWeight: '600', 
                    color: theme ? '#1f2937' : '#f3f4f6',
                    marginBottom: 4
                  }}>
                    {question.question}
                  </Text>
                  {question.required !== false && (
                    <Text style={{ 
                      fontSize: 11, 
                      color: theme ? '#6b7280' : '#9ca3af',
                      fontStyle: 'italic'
                    }}>
                      {isMultiSelect ? 'Select one or more options' : 'Select one option'} (required)
                    </Text>
                  )}
                </View>

                {/* Options */}
                <View style={{ gap: 8 }}>
                  {question.options.map((option, optionIndex) => {
                    const isSelected = isOptionSelected(question.id, option);
                    
                    return (
                      <Pressable
                        key={`${question.id}-${optionIndex}`}
                        onPress={() => handleOptionToggle(question.id, option)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: isSelected 
                            ? (theme ? '#dbeafe' : '#1e3a5f') 
                            : (theme ? 'white' : '#1e293b'),
                          borderRadius: 10,
                          padding: 12,
                          borderWidth: 1.5,
                          borderColor: isSelected 
                            ? (theme ? '#3b82f6' : '#60a5fa')
                            : (theme ? '#e5e7eb' : '#374151'),
                          shadowColor: isSelected ? '#3b82f6' : '#000',
                          shadowOffset: { width: 0, height: 1 },
                          shadowOpacity: isSelected ? 0.2 : 0.05,
                          shadowRadius: 2,
                          elevation: isSelected ? 2 : 1,
                        }}
                      >
                        {/* Selection Indicator */}
                        <View style={{
                          width: 20,
                          height: 20,
                          borderRadius: isMultiSelect ? 4 : 10,
                          borderWidth: 2,
                          borderColor: isSelected 
                            ? (theme ? '#3b82f6' : '#60a5fa')
                            : (theme ? '#9ca3af' : '#6b7280'),
                          backgroundColor: isSelected 
                            ? (theme ? '#3b82f6' : '#60a5fa')
                            : 'transparent',
                          marginRight: 12,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}>
                          {isSelected && (
                            isMultiSelect ? (
                              <AntDesign name="check" size={12} color="white" />
                            ) : (
                              <View style={{
                                width: 8,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'white'
                              }} />
                            )
                          )}
                        </View>

                        {/* Option Text */}
                        <Text style={{
                          flex: 1,
                          fontSize: 14,
                          color: isSelected 
                            ? (theme ? '#1e40af' : '#dbeafe')
                            : (theme ? '#1f2937' : '#e5e7eb'),
                          fontWeight: isSelected ? '600' : '400'
                        }}>
                          {option}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </ScrollView>

        {/* Submit Button */}
        <Pressable
          onPress={handleSubmit}
          disabled={!canSubmit()}
          style={{
            marginTop: 16,
            paddingVertical: 12,
            paddingHorizontal: 20,
            borderRadius: 10,
            backgroundColor: canSubmit() 
              ? (theme ? '#3b82f6' : '#2563eb')
              : (theme ? '#9ca3af' : '#4b5563'),
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: 8,
            opacity: canSubmit() ? 1 : 0.6,
          }}
        >
          <AntDesign 
            name="check-circle" 
            size={16} 
            color="white" 
          />
          <Text style={{ 
            color: 'white', 
            fontSize: 15, 
            fontWeight: '600' 
          }}>
            Submit Answers
          </Text>
        </Pressable>

        {/* Validation Message */}
        {!canSubmit() && (
          <Text style={{
            marginTop: 8,
            fontSize: 12,
            color: theme ? '#ef4444' : '#f87171',
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            Please answer all required questions
          </Text>
        )}
      </View>
    </Animatable.View>
  );
});

QuestionMessage.displayName = 'QuestionMessage';

export default QuestionMessage;















